import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, BarChart3, Wallet, Upload, Lightbulb, BookOpen, TrendingUp, Wifi, WifiOff, ClipboardList, Settings, ChevronDown } from 'lucide-react';
import { useTradingAnalyses, useTradingConfirmations, useTradingMetrics, useTradingTrades, useAccountLatest, useAnalysisJobs, useRetryJob } from '../hooks/useTradingAnalysis';
import { useTradingWebSocket } from '../hooks/useTradingWebSocket';
import SignalCard from '../components/trading/SignalCard';
import LiveSignalFeed from '../components/trading/LiveSignalFeed';
import TradeTable from '../components/trading/TradeTable';
import AnalysisJobQueue from '../components/trading/AnalysisJobQueue';
import type { TradingAnalysis, TradingConfirmation } from '../types/trading';

/** Map a TradingConfirmation to TradingAnalysis shape for the signal feed. */
function confirmationToAnalysis(c: TradingConfirmation): TradingAnalysis {
  return {
    id: c.id,
    timestamp: c.timestamp,
    symbol: c.symbol,
    timeframe: c.timeframe,
    signal: c.signal,
    confidence: c.confidence,
    entry: c.entry,
    stop_loss: c.stop_loss,
    take_profit: c.take_profit,
    take_profit_2: c.take_profit_2,
    reasoning: c.reasoning,
    warnings: c.warnings,
    session: c.session,
    timeframe_alignment: c.timeframe_alignment,
    key_levels: c.key_levels,
    fair_value_gaps: c.fair_value_gaps,
    indicators: c.indicators,
    cached: false,
    provider: c.provider,
    model_used: c.model_used,
    tokens_used: c.tokens_used,
    cost_usd: c.cost_usd,
    user_rating: c.user_rating,
    user_notes: c.user_notes,
    rated_at: c.rated_at,
    validation_status: null,
    validation_confidence: null,
    validation_reasoning: null,
    validation_provider: null,
    validation_model: null,
    validated_at: null,
    // Extra confirmation metadata surfaced via source tag
    _source: 'confirmation' as const,
    _confirmed: c.confirmed,
    _ea_name: c.ea_name,
    _analysis_mode: c.analysis_mode,
    _signal_source: c.signal_source,
    _confluence_sources: c.confluence_sources,
    _contra_signal: c.contra_signal,
    _contra_confidence: c.contra_confidence,
    _contra_entry: c.contra_entry,
    _contra_stop_loss: c.contra_stop_loss,
    _contra_take_profit: c.contra_take_profit,
    _contra_reasoning: c.contra_reasoning,
  } as TradingAnalysis;
}

const TradingDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { data: analyses = [], isLoading: loadingAnalyses } = useTradingAnalyses();
  const { data: confirmations = [], isLoading: loadingConfirmations } = useTradingConfirmations();
  const { data: metrics } = useTradingMetrics();
  const { data: trades = [] } = useTradingTrades();
  const { data: account } = useAccountLatest();
  const { data: jobsData } = useAnalysisJobs({ limit: 10 });
  const retryJob = useRetryJob();
  const { connected, lastSignal, alerts } = useTradingWebSocket();
  const [accountExpanded, setAccountExpanded] = React.useState(false);
  const [perfExpanded, setPerfExpanded] = React.useState(false);

  // Merge analyses and confirmations into a single feed, sorted by timestamp desc
  const allSignals: TradingAnalysis[] = React.useMemo(() => {
    const mapped = confirmations.map(confirmationToAnalysis);
    const combined = [...analyses, ...mapped];
    combined.sort((a, b) => {
      const tA = new Date(a.timestamp.replace(/^(\d{4})\.(\d{2})\.(\d{2})/, '$1-$2-$3')).getTime();
      const tB = new Date(b.timestamp.replace(/^(\d{4})\.(\d{2})\.(\d{2})/, '$1-$2-$3')).getTime();
      return tB - tA;
    });
    return combined;
  }, [analyses, confirmations]);

  // Prefer API-fetched analysis over WS signal — WS signal is partial and
  // doesn't update after feedback, so only use it if no API data is available
  // or if it's newer than the latest fetched analysis.
  const latestAnalysis = (() => {
    const fetched = allSignals[0] || null;
    if (!lastSignal) return fetched;
    if (!fetched) return lastSignal;
    // If the WS signal matches the latest fetched analysis, prefer fetched (has user_rating etc.)
    if (lastSignal.id === fetched.id) return fetched;
    // WS signal is a different/newer analysis — use it
    return lastSignal;
  })();

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white">Trading Analyst</h1>
            <p className="text-slate-400 text-sm">AI-powered chart analysis with LLM Vision</p>
          </div>
          <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${
            connected ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-700 text-slate-400'
          }`}>
            {connected ? <Wifi size={12} /> : <WifiOff size={12} />}
            {connected ? 'Live' : 'Offline'}
          </span>
        </div>
        <button
          onClick={() => navigate('/trading/analyze')}
          className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-lg transition-colors"
        >
          <Upload size={16} />
          Manual Analysis
        </button>
      </div>

      {/* Sub-page Navigation */}
      <div className="flex gap-2 flex-wrap">
        {[
          { label: 'Trades', icon: ClipboardList, path: '/trading/trades' },
          { label: 'Insights', icon: Lightbulb, path: '/trading/insights' },
          { label: 'Skills', icon: BookOpen, path: '/trading/skills' },
          { label: 'Performance', icon: TrendingUp, path: '/trading/performance' },
          { label: 'Settings', icon: Settings, path: '/trading/settings' },
        ].map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm rounded-lg transition-colors border border-slate-700/50"
            >
              <Icon size={14} />
              {item.label}
            </button>
          );
        })}
      </div>

      {/* WebSocket Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.slice(0, 3).map((alert, idx) => (
            <div key={idx} className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-2 text-sm text-amber-300">
              <Activity size={14} />
              <span>{alert.message || `${alert.type} event received`}</span>
              <span className="ml-auto text-xs text-amber-500">{new Date(typeof alert.timestamp === 'string' && !alert.timestamp.endsWith('Z') ? alert.timestamp + 'Z' : alert.timestamp).toLocaleTimeString()}</span>
            </div>
          ))}
        </div>
      )}

      {/* Analysis Job Queue */}
      {jobsData && jobsData.jobs.length > 0 && (
        <AnalysisJobQueue
          jobs={jobsData.jobs}
          total={jobsData.total}
          onRetry={(jobId) => retryJob.mutate(jobId)}
          retrying={retryJob.isPending}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Latest Signal */}
        <div className="lg:col-span-2">
          <h2 className="text-sm font-medium text-slate-400 mb-3">Latest Signal</h2>
          {loadingAnalyses || loadingConfirmations ? (
            <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-8 text-center">
              <div className="w-6 h-6 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin mx-auto" />
            </div>
          ) : latestAnalysis ? (
            <SignalCard analysis={latestAnalysis} />
          ) : (
            <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-8 text-center">
              <Activity size={32} className="mx-auto mb-3 text-slate-600" />
              <p className="text-slate-400">No analyses yet</p>
              <p className="text-sm text-slate-500 mt-1">
                Connect your MT5 EA or upload a chart screenshot
              </p>
            </div>
          )}
        </div>

        {/* Account + Performance Summary */}
        <div className="space-y-4">
          {/* Account Balance Card */}
          {account && (
            <div>
              <button
                onClick={() => setAccountExpanded(v => !v)}
                className="flex items-center justify-between w-full text-sm font-medium text-slate-400 mb-3 hover:text-slate-300 transition-colors"
              >
                <span>Account</span>
                <ChevronDown size={14} className={`transition-transform duration-200 ${accountExpanded ? 'rotate-180' : ''}`} />
              </button>
              {accountExpanded && <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 text-sm">Balance</span>
                  <span className="text-white font-semibold">${account.balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 text-sm">Equity</span>
                  <span className="text-white font-semibold">${account.equity.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                {account.free_margin !== null && (
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-sm">Free Margin</span>
                    <span className="text-slate-300 text-sm">${account.free_margin.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                )}
                {account.margin_level !== null && account.margin_level > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-sm">Margin Level</span>
                    <span className="text-slate-300 text-sm">{account.margin_level.toFixed(1)}%</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 text-sm">Open Positions</span>
                  <span className="text-slate-300 text-sm">{account.open_positions}</span>
                </div>
                <div className="flex justify-between items-center border-t border-slate-700/50 pt-3">
                  <span className="text-slate-400 text-sm">Daily P/L</span>
                  <span className={`font-semibold ${(account.daily_pnl ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {(account.daily_pnl ?? 0) >= 0 ? '+' : ''}${(account.daily_pnl ?? 0).toFixed(2)}
                  </span>
                </div>
              </div>}
            </div>
          )}

          <div>
            <button
              onClick={() => setPerfExpanded(v => !v)}
              className="flex items-center justify-between w-full text-sm font-medium text-slate-400 mb-3 hover:text-slate-300 transition-colors"
            >
              <span>Performance Summary</span>
              <ChevronDown size={14} className={`transition-transform duration-200 ${perfExpanded ? 'rotate-180' : ''}`} />
            </button>
            {perfExpanded && <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-3">
              {metrics ? (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-sm">Total Trades</span>
                    <span className="text-white font-semibold">{metrics.total_trades}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-sm">Win Rate</span>
                    <span className={`font-semibold ${(metrics.win_rate || 0) >= 50 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {metrics.win_rate !== null ? `${metrics.win_rate.toFixed(1)}%` : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-sm">Net P/L</span>
                    <span className={`font-semibold ${metrics.net_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      ${metrics.net_profit.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-sm">Profit Factor</span>
                    <span className="text-white font-semibold">
                      {metrics.profit_factor !== null ? metrics.profit_factor.toFixed(2) : '—'}
                    </span>
                  </div>
                  {metrics.max_drawdown !== null && (
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400 text-sm">Max Drawdown</span>
                      <span className="text-red-400 font-semibold">${metrics.max_drawdown.toFixed(2)}</span>
                    </div>
                  )}
                  {metrics.avg_trade_duration !== null && (
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400 text-sm">Avg Duration</span>
                      <span className="text-slate-300 text-sm">{metrics.avg_trade_duration.toFixed(0)}m</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center border-t border-slate-700/50 pt-3">
                    <span className="text-slate-400 text-sm flex items-center gap-1">
                      <Wallet size={14} /> API Cost
                    </span>
                    <span className="text-slate-300 text-sm">${metrics.total_api_cost.toFixed(4)}</span>
                  </div>
                  {metrics.signal_distribution && (
                    <div className="border-t border-slate-700/50 pt-3">
                      <span className="text-slate-400 text-xs">Signal Distribution</span>
                      <div className="flex gap-2 mt-1">
                        {Object.entries(metrics.signal_distribution).map(([sig, count]) => (
                          <span key={sig} className={`text-xs px-2 py-0.5 rounded ${
                            sig === 'BUY' ? 'bg-emerald-500/10 text-emerald-400' :
                            sig === 'SELL' ? 'bg-red-500/10 text-red-400' :
                            'bg-slate-700 text-slate-400'
                          }`}>
                            {sig}: {count}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-4">
                  <BarChart3 size={24} className="mx-auto mb-2 text-slate-600" />
                  <p className="text-sm text-slate-500">No performance data yet</p>
                </div>
              )}
            </div>}
          </div>
        </div>
      </div>

      {/* Signal Feed */}
      <div>
        <h2 className="text-sm font-medium text-slate-400 mb-3">Recent Signals</h2>
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
          <LiveSignalFeed analyses={allSignals} />
        </div>
      </div>

      {/* Trade History */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-slate-400">Recent Trades</h2>
          <button
            onClick={() => navigate('/trading/trades')}
            className="text-xs text-teal-400 hover:text-teal-300"
          >
            View All
          </button>
        </div>
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
          <TradeTable trades={trades.slice(0, 10)} />
        </div>
      </div>

    </div>
  );
};

export default TradingDashboard;
